import React, { useState, useContext, useEffect, Fragment, useRef, useMemo } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { toast } from "../../utils/toast";
import AppContext from "../../context/AppContext";
import CreateOrganization from "./CreateOrgnization";
import {
  fetchOrgnizationData,
  fetchOrganizationById,
  deleteOrganization,
  getAllByOrganizationId,
  getProjectList,
  updateProjectStatus,
  updateProjectDeleteStatus,
} from "../../service/User";
import Updateorgnization from "./Updateorgnization";
import { Spinner } from "../../Spinner";
import translations from "../common/Translations";
import PageSizeSelector from "../common/PageSizeSelector";
import CreateDepartment from "./CreateDeparment";
import CreateProject from "./CreateProject";
import EditProject from "./EditProject";
import CommonButton from "../common/CommonButton";
import CommonTooltip from "../common/CommonTooltip";
import { Button } from "../common/Button";
import { FaChevronDown, FaChevronRight, FaChevronLeft, FaFolderPlus } from "react-icons/fa";
import { LuBuilding2 } from "react-icons/lu";
import { Eye, EyeOff, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { getPermissionByScreenCode, canShowActionColumn, DEPARTMENT_SCREEN_CODE, PROJECT_SCREEN_CODE, DCRC_CONFIG_SCREEN_CODE } from "../../utils/permissions";
import { isRTL } from "../../utils/rtl";
import { getThemedTableShell } from "../../utils/tableStyles";
import ActionIconButton from "../common/ActionIconButton";
import PagerArrowButton from "../common/PagerArrowButton";

const sortByNameKey = (items, key = "name") =>
  [...(items || [])].sort((a, b) =>
    String(a?.[key] ?? "").localeCompare(String(b?.[key] ?? ""), undefined, {
      sensitivity: "base",
      numeric: true,
    })
  );

const sortOrganizations = (items) => sortByNameKey(items, "organizationName");
const sortDepartments = (items) => sortByNameKey(items, "name");
const sortProjects = (items) => sortByNameKey(items, "name");

const nameCellStyle = {
  display: "-webkit-box",
  WebkitLineClamp: 3,
  WebkitBoxOrient: "vertical",
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
};

const NameCell = ({ children, indent = 0 , dataTestId }) => (
  <span className="block overflow-hidden" style={{ ...nameCellStyle, paddingInlineStart: indent }} data-testid={dataTestId}>
    {children}
  </span>
);

const TypeBadge = ({ level, langMode }) => {
  const label =
    level === "organization"
      ? translations[langMode].org
      : level === "department"
        ? translations[langMode].department
        : translations[langMode].project;
  const colorClass =
    level === "organization"
      ? "bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200"
      : level === "department"
        ? "bg-teal-100 text-teal-800 dark:bg-teal-900/50 dark:text-teal-200"
        : "bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200";
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-semibold whitespace-nowrap ${colorClass}`}>
      {label}
    </span>
  );
};

const ExpandChevron = ({ expanded, isRtl }) =>
  expanded ? (
    <FaChevronDown className="h-3.5 w-3.5 flex-shrink-0 text-gray-500" aria-hidden />
  ) : isRtl ? (
    <FaChevronLeft className="h-3.5 w-3.5 flex-shrink-0 text-gray-500" aria-hidden />
  ) : (
    <FaChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-gray-500" aria-hidden />
  );

const RowActions = ({ children }) => (
  <div
    className="flex flex-nowrap justify-end items-center gap-2 sm:gap-3 whitespace-nowrap min-w-max ms-auto"
    onClick={(e) => e.stopPropagation()}
  >
    {children}
  </div>
);

const IconTooltip = ({ text, children }) => (
  <CommonTooltip
    text={text}
    position="top"
    disableDefaultDescription
    tooltipClassName="w-max max-w-[min(15rem,calc(100vw-1.5rem))] [text-wrap:balance]"
    fitContent
    clampToViewport
    hoverOnly
  >
    {children}
  </CommonTooltip>
);

const ActionIconBtn = ({ onClick, title, className, children, isRtl }) => (
  <IconTooltip text={title} isRtl={isRtl}>
    <ActionIconButton
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(e);
      }}
      ariaLabel={title}
      tone="gray"
      className={`!h-7 !w-7 !rounded-md border ${className}`}
    >
      {children}
    </ActionIconButton>
  </IconTooltip>
);

const EditActionIcon = ({ onClick, title, isRtl, dataTestId }) => (
  <IconTooltip text={title} isRtl={isRtl}>
    <ActionIconButton
      type="button"
      data-testid={dataTestId}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(e);
      }}
      ariaLabel={title}
      tone="blue"
      className="!h-7 !w-7 !rounded-md"
    >
      <Pencil size={14} />
    </ActionIconButton>
  </IconTooltip>
);

const DeleteActionIcon = ({ onClick, title, isRtl, dataTestId }) => (
  <IconTooltip text={title} isRtl={isRtl}>
    <ActionIconButton
      type="button"
      data-testid={dataTestId}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(e);
      }}
      ariaLabel={title}
      tone="red"
      className="!h-7 !w-7 !rounded-md"
    >
      <Trash2 size={14} />
    </ActionIconButton>
  </IconTooltip>
);

const AddDepartmentIcon = ({ onClick, title, isRtl }) => (
  <ActionIconBtn
    onClick={onClick}
    title={title}
    isRtl={isRtl}
    className="border-teal-200 bg-teal-50 text-teal-700 hover:bg-teal-100 dark:border-teal-800 dark:bg-teal-900/40 dark:text-teal-300 dark:hover:bg-teal-900/60"
  >
    <LuBuilding2 className="h-4 w-4" />
  </ActionIconBtn>
);

const AddProjectIcon = ({ onClick, title, isRtl }) => (
  <ActionIconBtn
    onClick={onClick}
    title={title}
    isRtl={isRtl}
    className="border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-900/40 dark:text-amber-300 dark:hover:bg-amber-900/60"
  >
    <FaFolderPlus className="h-4 w-4" />
  </ActionIconBtn>
);

const VIEW_MODE_ORGANIZATION = "organization";
const VIEW_MODE_DEPARTMENT_ONLY = "department_only";
const VIEW_MODE_PROJECT_ONLY = "project_only";
const VIEW_MODE_DEPARTMENT_PROJECT = "department_project";

const OrgnizationList = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalValue, setTotalValue] = useState(0);
  const totalPages = Math.ceil(totalValue / pageSize) - 1;
  const startIndex = currentPage * pageSize;
  const endIndex = startIndex + pageSize;
  const totalItems = totalValue;
  const displayStart = totalItems > 0 ? startIndex + 1 : 0;
  const displayEnd = totalItems > 0 ? Math.min(endIndex, totalItems) : 0;
  const { theme, langMode, userType, selectedOrgId, setSelectedOrgId, setSelectedOrgName } = useContext(AppContext);
  const isRtl = isRTL(langMode);
  const deptPerm = getPermissionByScreenCode(DEPARTMENT_SCREEN_CODE);
  const projPerm = getPermissionByScreenCode(PROJECT_SCREEN_CODE);
  const dcrcPerm = getPermissionByScreenCode(DCRC_CONFIG_SCREEN_CODE);
  const hasDepartmentAccess = Boolean(deptPerm?.canView);
  const hasProjectAccess = Boolean(projPerm?.canView);
  const isSuperAdmin = userType === "super_admin";
  const isAdmin = userType === "admin";
  const isOfficeUser = userType === "office_user";
  const hasAnyDepartmentPermission =
    isSuperAdmin ||
    Boolean(
      deptPerm &&
        (deptPerm.canView || deptPerm.canCreate || deptPerm.canUpdate || deptPerm.canDelete)
    );
  const hasAnyProjectPermission =
    isSuperAdmin ||
    Boolean(
      projPerm &&
        (projPerm.canView || projPerm.canCreate || projPerm.canUpdate || projPerm.canDelete)
    );
  const canCreateDepartment = isSuperAdmin || Boolean(deptPerm?.canCreate);
  const canUpdateDepartment = isSuperAdmin || Boolean(deptPerm?.canUpdate);
  const canCreateProject = isSuperAdmin || Boolean(projPerm?.canCreate);
  const canUpdateProject = isSuperAdmin || Boolean(projPerm?.canUpdate);
  const canDeleteProject = isSuperAdmin || Boolean(projPerm?.canDelete);
  const canUpdateDcrc = (isSuperAdmin || isAdmin || isOfficeUser) && Boolean(dcrcPerm?.canUpdate);
  const canViewDcrc =  (isSuperAdmin || isAdmin || isOfficeUser) && Boolean(dcrcPerm?.canView);
  const canDeleteDcrc = (isSuperAdmin || isAdmin || isOfficeUser) && Boolean(dcrcPerm?.canDelete);

  const viewMode = isSuperAdmin
    ? VIEW_MODE_ORGANIZATION
    : hasDepartmentAccess && hasProjectAccess
      ? VIEW_MODE_DEPARTMENT_PROJECT
      : hasDepartmentAccess
        ? VIEW_MODE_DEPARTMENT_ONLY
        : hasProjectAccess
          ? VIEW_MODE_PROJECT_ONLY
          : VIEW_MODE_ORGANIZATION;

  const showOrgActionColumn = isSuperAdmin && canShowActionColumn("USER_MANAGEMENT");
  const [open, setOpen] = useState(false);
  const [orgnizationData, setOrgnizationData] = useState([""]);
  const [orgnizationId, setOrgnizationID] = useState();
  const [editOrganizationname, setEditOrganizationName] = useState("");
  const [editOrganizationimg, setEditOrganizationImg] = useState(null);
  const [editReportEmails, setEditReportEmails] = useState([]);
  const [updateopen, setUpdateOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openDepartment, setOpenDepartment] = useState(false);
  const [openProject, setOpenProject] = useState(false);
  const [openEditProject, setOpenEditProject] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [editProjectDepartmentId, setEditProjectDepartmentId] = useState("");
  const [getDeparment, setgeDeparment] = useState([]);
  const [projectData, setProjectData] = useState([]);
  const [deparmentId, setDeparmentId] = useState("");
  const [openOrganizationId, setOpenOrganizationId] = useState(null);
  const [openDepartmentId, setOpenDepartmentId] = useState(null);
  const [editDepartmentId, setEditDepartmentId] = useState(null);
  const [editDepartmentName, setEditDepartmentName] = useState("");
  const [organizationSearchTerm, setOrganizationSearchTerm] = useState("");
  const [isIms, setIsIMS] = useState(false);
  const [togglingProjectId, setTogglingProjectId] = useState(null);
  const [togglingDeleteProjectId, setTogglingDeleteProjectId] = useState(null);
  const [deleteConfirmProject, setDeleteConfirmProject] = useState(null);
  const [deleteConfirmOrganization, setDeleteConfirmOrganization] = useState(null);
  const [isDeletingOrganization, setIsDeletingOrganization] = useState(false);
  const deleteOrgModalInitialFocusRef = useRef(null);
  const toggleOrganization = (orgId) => {
    setOpenOrganizationId((prevId) => (prevId === orgId ? null : orgId));
  };
  const toggleDepartment = (deptId) => {
    setOpenDepartmentId((prevId) => (prevId === deptId ? null : deptId));
  };

  const totalPagesCount = Math.ceil(totalItems / pageSize);
  const paginationArray = Array.from({ length: totalPagesCount }, (_, index) =>
    (index + 1).toString()
  );
  const handleEditOrganization = async (orgId) => {
    setOrgnizationID(orgId);
    try {
      const ResponseData = await fetchOrganizationById(orgId);
      setEditOrganizationName(ResponseData.data.body.value.organizationName);
      setEditOrganizationImg(ResponseData.data.body.value.logo);
      setIsIMS(ResponseData.data.body.value.ims);
      setUpdateOpen(true);
    } catch (error) {
      console.error("Error fetching organization data:", error);
      toast.error(translations[langMode].failstofetchorg);
    }
  };

  const sortedOrganizations = sortOrganizations(orgnizationData);
  const filteredSortedOrganizations = useMemo(() => {
    const query = String(organizationSearchTerm || "").trim().toLowerCase();
    if (!query || viewMode !== VIEW_MODE_ORGANIZATION) return sortedOrganizations;
    return sortedOrganizations.filter((item) =>
      String(item?.organizationName || "").toLowerCase().includes(query)
    );
  }, [organizationSearchTerm, sortedOrganizations, viewMode]);
  const sortedDepartments = sortDepartments(getDeparment);
  const sortedProjects = sortProjects(projectData);

  const getProjectsForDepartment = (departmentId) =>
    sortProjects((projectData || []).filter((p) => p?.departmentId === departmentId));

  const getDepartmentsForOrganization = (organizationId) =>
    sortDepartments(
      (getDeparment || []).filter((d) => d?.organizationId === organizationId)
    );

  const showOrgListActionColumn =
    showOrgActionColumn || canCreateDepartment || canCreateProject;
  const showDeptListActionColumn = canCreateProject || canUpdateDepartment;
  const showDeptProjectListActionColumn =
    canCreateProject || canUpdateDepartment || canUpdateProject || canUpdateDcrc || canViewDcrc;

  const handlePageSizeChange = (value) => {
    const size = parseInt(value, 10);
    setPageSize(size);
    setCurrentPage(0);
  };

  const goToPreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const fetchorgnizationlist = async () => {
    try {
      setLoading(true);
      const responseData = await fetchOrgnizationData(currentPage, pageSize);
      const value = responseData?.data?.body?.value || {};
      const organizationList = Array.isArray(value.organizationList) ? value.organizationList : [];
      const totalSize = Number(value.totalSize || 0);
      setOrgnizationData(organizationList);
      setTotalValue(totalSize);
      return { organizationList, totalSize };
    } catch (error) {
      console.error("Error fetching organization data:", error);
      return { organizationList: [], totalSize: 0 };
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchorgnizationlist();
  }, [currentPage, pageSize]);

  useEffect(() => {
    const fetchDepartments = async () => {
     
      if (isSuperAdmin) {
        if (
          orgnizationData &&
          orgnizationData !== undefined &&
          orgnizationData.length > 0
        ) {
          const allDepartments = await Promise.all(
            orgnizationData.map(async (item) => {
              if (item.organizationId !== undefined) {
                return await getAllByOrganizationId(item.organizationId);
              }
            })
          );
          setgeDeparment((allDepartments.filter(Boolean).flat()));
        } else {
          setgeDeparment([]);
        }
      } else {
        const orgId = localStorage.getItem("selectedOrgId");
        if (orgId) {
          try {
            const departments = await getAllByOrganizationId(orgId);
            setgeDeparment(Array.isArray(departments) ? departments : []);
          } catch (err) {
            console.error("Error fetching departments for organization:", err);
            setgeDeparment([]);
          }
        } else {
          setgeDeparment([]);
        }
      }
    };

    fetchDepartments();
  }, [
    isSuperAdmin,
    selectedOrgId,
    orgnizationData,
    currentPage,
    pageSize,
    hasAnyDepartmentPermission,
    hasAnyProjectPermission,
  ]);

  useEffect(() => {
    const fetchProjects = async () => {
    
      if (
        getDeparment &&
        getDeparment !== undefined &&
        getDeparment.length > 0
      ) {
        const allProjects = await Promise.all(
          getDeparment.map(async (item) => {
            if (item?.id !== undefined) {
              return await getProjectList(item.id);
            }
          })
        );
        setProjectData(allProjects.flat());
      } else {
        setProjectData([]);
      }
    };

    fetchProjects();
  }, [getDeparment, currentPage, pageSize,]);

  const handleProjectStatusToggle = async (project, e) => {
    e?.stopPropagation?.();
    if (!canUpdateDcrc) {
      toast.error("You do not have permission to update DCRC");
      return;
    }
    if (!project?.id || togglingProjectId === project.id) return;
    const newEnabled = !(project?.isDcrcEnabled ?? false);
    setTogglingProjectId(project.id);
    try {
      await updateProjectStatus(project.id, newEnabled);
      setProjectData((prev) =>
        prev.map((p) =>
          p.id === project.id ? { ...p, isDcrcEnabled: newEnabled } : p
        )
      );
      toast.success(
        newEnabled
          ? translations[langMode].Enable
          : translations[langMode].disable
      );
    } catch (err) {
      toast.error(err?.response?.data?.body?.value || err?.message || translations[langMode].orgFailedUpdateStatus || "Failed to update status");
    } finally {
      setTogglingProjectId(null);
    }
  };

  const handleProjectDeleteToggle = async (project, e) => {
    e?.stopPropagation?.();
    if (!project?.id || togglingDeleteProjectId === project.id) return;
    const newDeleted = !(project?.isDeleted ?? false);
    setDeleteConfirmProject(null);
    setTogglingDeleteProjectId(project.id);
    try {
      await updateProjectDeleteStatus(project.id, newDeleted);
      setProjectData((prev) =>
        prev.map((p) => (p.id === project.id ? { ...p, isDeleted: newDeleted } : p))
      );
      toast.success(
        newDeleted
          ? (translations[langMode]?.orgProjectHidden ?? "Project hidden")
          : (translations[langMode]?.orgProjectVisible ?? "Project visible again")
      );
    } catch (err) {
      toast.error(err?.response?.data?.body?.value || err?.message || translations[langMode].orgFailedUpdateStatus || "Failed to update delete status");
    } finally {
      setTogglingDeleteProjectId(null);
    }
  };

  const handleOrganizationDeleteConfirm = async () => {
    if (isDeletingOrganization) return;
    const deletingOrgId = deleteConfirmOrganization?.organizationId;
    if (!deletingOrgId && deletingOrgId !== 0) {
      setDeleteConfirmOrganization(null);
      return;
    }

    setIsDeletingOrganization(true);
    try {
      const response = await deleteOrganization(deletingOrgId);
      const backendMessage = response?.body?.value || translations[langMode]?.orgDeleteOrgSuccess || "Organization deleted successfully";

      const { organizationList } = await fetchorgnizationlist();
      const selectedOrgClean = String(selectedOrgId ?? "").replace(/"/g, "");
      const deletedOrgClean = String(deletingOrgId).replace(/"/g, "");

      if (selectedOrgClean && selectedOrgClean === deletedOrgClean) {
        const sortedOrgs = sortOrganizations(organizationList);
        const fallbackOrg = sortedOrgs[0];

        localStorage.removeItem("departmentId");
        localStorage.removeItem("projectId");
        localStorage.removeItem("activityId");
        localStorage.removeItem("selectedActivity");

        if (fallbackOrg?.organizationId !== undefined && fallbackOrg?.organizationId !== null) {
          const nextOrgId = Number(fallbackOrg.organizationId);
          const nextOrgName = fallbackOrg.organizationName ?? "";
          setSelectedOrgId(nextOrgId);
          setSelectedOrgName(nextOrgName);
          localStorage.setItem("selectedOrgId", nextOrgId);
          localStorage.setItem("selectedOrgName", nextOrgName);
        } else {
          setSelectedOrgId("");
          setSelectedOrgName("");
          localStorage.removeItem("selectedOrgId");
          localStorage.removeItem("selectedOrgName");
        }
      }

      setDeleteConfirmOrganization(null);
      toast.success(backendMessage);
    } catch (error) {
      const backendMessage =
        error?.response?.data?.body?.value ||
        error?.message ||
        translations[langMode]?.orgFailedDeleteOrg ||
        "Failed to delete organization";
      toast.error(backendMessage);
    } finally {
      setIsDeletingOrganization(false);
    }
  };

  const tableStyles = getThemedTableShell(theme);
  const deleteOrganizationTitle =
    translations[langMode]?.deleteOrganizationTitle || "Delete organization?";
  const editOrganizationTooltip =
    translations[langMode]?.editOrganizationTooltip || "Edit organization";
  const deleteOrganizationTooltip =
    translations[langMode]?.deleteOrganizationTooltip || "Delete organization";
  const deleteOrganizationMessage =
    translations[langMode]?.deleteOrganizationCascadeMessage ||
    "Are you sure you want to delete this organization? All dependent data linked to it will also be removed.";

  const titleLabel =
    viewMode === VIEW_MODE_ORGANIZATION
      ? (translations[langMode].organizationPageTitle ??
        translations[langMode].org)
      : viewMode === VIEW_MODE_DEPARTMENT_ONLY
        ? (translations[langMode].department ?? "Department")
        : viewMode === VIEW_MODE_PROJECT_ONLY
          ? (translations[langMode].project ?? "Project")
          : (translations[langMode].org ?? "Organization");
  const organizationSearchPlaceholder =
    translations[langMode]?.organizationPageSearchPlaceholder ??
    `${translations[langMode]?.search || "Search"} ${translations[langMode]?.org || "Organization"}...`;
  const organizationHierarchyHint =
    translations[langMode]?.organizationPageHierarchyHint ??
    translations[langMode].orgHierarchyHint;

  return (
    <div
      className={`flex h-full min-h-0 w-full flex-col overflow-x-hidden ${theme === "dark"
        ? "dark-mode"
        : theme === "high-contrast"
          ? "high-contrast"
          : ""
        }`}
    >
      <div
        className={`mx-2 mt-2 flex min-w-0 shrink-0 flex-col gap-3 rounded-xl border border-slate-200 bg-white px-3 py-3 shadow-sm sm:mx-4 sm:mt-3 sm:gap-4 sm:px-5 sm:py-4 ${theme === "dark"
          ? "dark-mode"
          : theme === "high-contrast"
            ? "high-contrast"
            : ""
          }`}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1
              className={`text-base font-bold text-slate-800 leading-tight sm:text-lg ${theme === "dark"
                ? "dark-mode "
                : theme === "high-contrast"
                  ? "high-contrast  "
                  : ""
                }`}
              data-testid="organization-list-title"
            >
              {titleLabel}
            </h1>
            {viewMode === VIEW_MODE_ORGANIZATION && (
              <p
                className={`text-xs mt-0.5 ${theme === "dark" ? "text-gray-400" : "text-slate-500"}`}
                data-testid="organization-list-hierarchy-hint"
              >
                {organizationHierarchyHint}
              </p>
            )}
          </div>
          {viewMode === VIEW_MODE_ORGANIZATION && (
            <div className="w-full sm:w-auto">
              <div className="flex w-full items-center gap-2 sm:w-auto">
                <div className="relative w-full sm:w-[260px]">
                  <Search
                    size={14}
                    className={`absolute top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none ${isRtl ? "right-3" : "left-3"}`}
                  />
                  <input
                    type="text"
                    value={organizationSearchTerm}
                    onChange={(e) => setOrganizationSearchTerm(e.target.value)}
                    placeholder={organizationSearchPlaceholder}
                    className={`w-full h-9 rounded-lg border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-[#236DB4]/20 focus:border-[#236DB4] ${
                      isRtl ? "pr-9 pl-3 text-right" : "pl-9 pr-3"
                    }`}
                  />
                </div>
                <CommonButton colorIntent="primary"
                  aria-label={translations[langMode].addneworgnization}
                  onClick={() => setOpen(true)}
                  theme={theme}
                  variant="primary"
                  tooltipText={translations[langMode].addneworgnization || "Add new organization"}
                  tooltipPosition="top"
                  className="w-auto justify-center !whitespace-nowrap"
                >
                  <Plus size={16} />
                  {translations[langMode].addOrganization}
                </CommonButton>
              </div>
            </div>
          )}
          {viewMode === VIEW_MODE_DEPARTMENT_ONLY && canCreateDepartment && selectedOrgId && (
            <div className="w-full sm:w-auto">
              <Button
                isActive
                aria-label={translations[langMode].addnewdepartment}
                onClick={() => {
                  setOrgnizationID(selectedOrgId);
                  setEditDepartmentId(null);
                  setEditDepartmentName("");
                  setOpenDepartment(true);
                }}
                className="h-10 min-h-[40px] w-full justify-center rounded-[10px] px-3 py-2 text-xs sm:w-auto sm:px-4 sm:text-sm"
              >
                {translations[langMode].addDepartment}
              </Button>
            </div>
          )}
          {viewMode === VIEW_MODE_DEPARTMENT_PROJECT && canCreateDepartment && selectedOrgId && (
            <div className="w-full sm:w-auto">
              <Button
                isActive
                aria-label={translations[langMode].addnewdepartment}
                onClick={() => {
                  setOrgnizationID(selectedOrgId);
                  setEditDepartmentId(null);
                  setEditDepartmentName("");
                  setOpenDepartment(true);
                }}
                className="h-10 min-h-[40px] w-full justify-center rounded-[10px] px-3 py-2 text-xs sm:w-auto sm:px-4 sm:text-sm"
              >
                {translations[langMode].addDepartment}
              </Button>
            </div>
          )}
        </div>
      </div>
      <div
        className={`mx-2 mb-2 mt-2 flex min-h-[24rem] min-w-0 flex-none flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm sm:mx-4 sm:mb-3 sm:mt-3 sm:min-h-0 sm:flex-1 ${theme === "dark"
          ? "dark-mode"
          : theme === "high-contrast"
            ? "high-contrast"
            : ""
          }`}
      >
        {totalItems > 0 && (
        <div className="relative z-20 flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 bg-white px-3 py-2.5 sm:px-5">
          <p className="flex items-center gap-3 text-xs text-slate-500 sm:gap-4">
            <span className="hidden sm:inline whitespace-nowrap rtl-keep-ltr">
              {`${translations[langMode].showing || "Showing"} ${displayStart}-${displayEnd} ${translations[langMode].of || "of"} ${totalItems}`}
            </span>
          </p>
          <div className="flex min-w-0 items-center gap-2 text-xs text-slate-500 sm:gap-3">
            <div className="flex items-center gap-1.5">
              <label htmlFor="pageSize" className="whitespace-nowrap">{translations[langMode].rows}</label>
              <PageSizeSelector value={pageSize} onChange={handlePageSizeChange} height="32px" fontSize="12px" />
            </div>
            <span className="pager-nav flex items-center gap-1">
              <PagerArrowButton
                direction="prev"
                isRtl={isRtl}
                onClick={goToPreviousPage}
                disabled={currentPage === 0}
                ariaLabel={translations[langMode]?.previousPage || "Previous page"}
              />
              <span className="text-xs text-slate-600 px-1 whitespace-nowrap">
                {currentPage + 1} / {Math.max(totalPages + 1, 1)}
              </span>
              <PagerArrowButton
                direction="next"
                isRtl={isRtl}
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                ariaLabel={translations[langMode]?.nextPage || "Next page"}
              />
            </span>
          </div>
        </div>
        )}
        <div className="task-list-scrollbar mobile-hide-scrollbar rtl-table-scroll min-w-0 w-full flex-1 min-h-0 max-h-[500px] overflow-x-auto overflow-y-auto">
                <table className={`${tableStyles.table} min-w-[760px] rtl:text-right`} dir={isRtl ? "rtl" : "ltr"} data-testid="organization-list-table">
                  <thead
                    className={`sticky top-0 z-10 border-b text-xs font-semibold uppercase tracking-[0.04em] ${theme === "dark"
                      ? "dark-mode bg-gray-800 text-gray-300 border-gray-700"
                      : theme === "high-contrast"
                        ? "high-contrast bg-gray-100 text-gray-800 border-gray-300"
                        : "bg-slate-50 text-slate-500 border-slate-200"
                      }`}
                    data-testid="organization-list-table-thead"
                  >
                    <tr data-testid="organization-list-table-thead-tr">
                      {viewMode === VIEW_MODE_ORGANIZATION && (
                        <>
                          <th className="p-2 ml-5 align-middle bg-inherit w-12 text-center">S.L</th>
                          <th className="p-2 align-middle bg-inherit text-left rtl:text-right">{translations[langMode].recordName}</th>
                          <th className="p-2 align-middle bg-inherit w-28 text-center">{translations[langMode].recordType}</th>
                          <th className="p-2 align-middle bg-inherit w-24 text-center">{translations[langMode].logo}</th>
                          {showOrgListActionColumn && (
                            <th className="p-2 text-end align-middle bg-inherit min-w-[240px]">
                              {translations[langMode].Action}
                            </th>
                          )}
                        </>
                      )}
                      {viewMode === VIEW_MODE_DEPARTMENT_ONLY && (
                        <>
                          <th className="p-2 w-12 text-center">S.L</th>
                          <th className="p-2 text-left rtl:text-right">{translations[langMode].recordName}</th>
                          <th className="p-2 w-28 text-center">{translations[langMode].recordType}</th>
                          {showDeptListActionColumn && (
                            <th className="p-2 text-end min-w-[88px]">{translations[langMode].Action}</th>
                          )}
                        </>
                      )}
                      {viewMode === VIEW_MODE_PROJECT_ONLY && (
                        <>
                          <th className="p-2 w-12 text-center">S.L</th>
                          <th className="p-2 text-left rtl:text-right">{translations[langMode].recordName}</th>
                          <th className="p-2 w-28 text-center">{translations[langMode].recordType}</th>
                          {canUpdateProject || canViewDcrc ? (
                            <th className="p-2 text-end whitespace-nowrap">{translations[langMode].Action}</th>
                          ) : null}
                        </>
                      )}
                      {viewMode === VIEW_MODE_DEPARTMENT_PROJECT && (
                        <>
                          <th className="p-2 w-12 text-center">S.L</th>
                          <th className="p-2 text-left rtl:text-right">{translations[langMode].recordName}</th>
                          <th className="p-2 w-28 text-center">{translations[langMode].recordType}</th>
                          {showDeptProjectListActionColumn && (
                            <th className="p-2 text-end min-w-[88px]">{translations[langMode].Action}</th>
                          )}
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody className={`relative z-0 ${tableStyles.tbody} text-xs text-[#4B5563]`}>
                    {viewMode === VIEW_MODE_ORGANIZATION && filteredSortedOrganizations.length > 0 ? (
                      filteredSortedOrganizations.map((item, index) => {
                        const orgDepartments = getDepartmentsForOrganization(item.organizationId);
                        const isOrgExpanded = openOrganizationId === item.organizationId;
                        return (
                        <React.Fragment key={item.organizationId ?? index}>
                          <tr
                            className={`cursor-pointer ${theme === "dark"
                              ? "dark-mode bg-gray-700 border-gray-600"
                              : theme === "high-contrast"
                                ? "high-contrast bg-white"
                                : "bg-white"
                              } border-b hover:bg-blue-50/50 dark:hover:bg-gray-600/50`}
                            onClick={() => toggleOrganization(item.organizationId)}
                            data-testid={`organization-list-table-tr-${item.organizationId}`}
                          >
                            <td className="p-2 align-middle">
                              <div className="flex gap-2 items-center justify-center">
                                <ExpandChevron expanded={isOrgExpanded} isRtl={isRtl} />
                                <span>{index + 1}</span>
                              </div>
                            </td>
                            <td className="p-2.5 align-middle text-left rtl:text-right text-xs text-[#4B5563]" data-testid={`organization-list-table-td-organization-name-${item.organizationId}`}>
                              <NameCell>{item.organizationName}</NameCell>
                            </td>
                            <td className="p-2 align-middle text-center">
                              <TypeBadge level="organization" langMode={langMode} />
                            </td>
                            <td className="p-2 align-middle text-center" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center justify-center">
                                {item.logo ? (
                                  <img
                                    src={item.logo}
                                    alt={item.organizationName}
                                    className="h-10 w-10 rounded-full object-cover border border-gray-200"
                                  />
                                ) : (
                                  <span className="text-xs text-gray-400">?</span>
                                )}
                              </div>
                            </td>
                            {showOrgListActionColumn && (
                              <td className="p-2 align-middle text-end whitespace-nowrap">
                                <RowActions>
                                  {canCreateDepartment && (
                                    <AddDepartmentIcon
                                      isRtl={isRtl}
                                      title={translations[langMode].addnewdepartment}
                                      onClick={() => {
                                        setOrgnizationID(item.organizationId);
                                        setEditDepartmentId(null);
                                        setEditDepartmentName("");
                                        setOpenDepartment(true);
                                      }}
                                    />
                                  )}
                                  {showOrgActionColumn && (
                                    <EditActionIcon
                                      isRtl={isRtl}
                                      title={editOrganizationTooltip}
                                      onClick={() => handleEditOrganization(item.organizationId)}
                                    />
                                  )}
                                  {isSuperAdmin && (
                                    <DeleteActionIcon
                                      isRtl={isRtl}
                                      title={deleteOrganizationTooltip}
                                      dataTestId={`organization-list-table-td-action-delete-${item.organizationId}`}
                                      onClick={() => setDeleteConfirmOrganization(item)}
                                    />
                                  )}
                                </RowActions>
                              </td>
                            )}
                          </tr>
                          {isOrgExpanded &&
                            orgDepartments.map((department) => {
                              const deptProjects = getProjectsForDepartment(department?.id);
                              const isDeptExpanded = openDepartmentId === department?.id;
                              return (
                                <React.Fragment key={department?.id}>
                                  <tr
                                    className="cursor-pointer bg-slate-50/80 dark:bg-gray-800/60 text-[#4B5563] text-xs border-b border-b-[#EBECEF] dark:border-gray-600 hover:bg-[#EBF5FF] dark:hover:bg-gray-700/80"
                                    onClick={() => toggleDepartment(department?.id)}
                                  >
                                    <td className="p-2 align-middle">
                                      <div className="flex items-center justify-center ps-4">
                                        <ExpandChevron expanded={isDeptExpanded} isRtl={isRtl} />
                                      </div>
                                    </td>
                                    <td className="p-2 align-middle text-left rtl:text-right">
                                      <div className="flex items-center gap-2 ps-4">
                                        <NameCell dataTestId={`organization-list-table-td-department-name-${department.organizationId}-${department.id}`}>{department?.name}</NameCell>
                                        {deptProjects.length > 0 && (
                                          <span className="text-xs text-gray-400 whitespace-nowrap">
                                            ({deptProjects.length})
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                    <td className="p-2 align-middle text-center">
                                      <TypeBadge level="department" langMode={langMode} />
                                    </td>
                                    <td className="p-2 align-middle text-gray-400 text-xs text-center">?</td>
                                    {showOrgListActionColumn && (
                                      <td className="p-2 align-middle text-end whitespace-nowrap">
                                        <RowActions>
                                          {canCreateProject && (
                                            <AddProjectIcon
                                              isRtl={isRtl}
                                              title={translations[langMode].addnewproject}
                                              onClick={() => {
                                                setDeparmentId(department?.id);
                                                setOpenProject(true);
                                              }}
                                            />
                                          )}
                                          {showOrgActionColumn && canUpdateDepartment && (
                                            <EditActionIcon
                                              isRtl={isRtl}
                                              title={translations[langMode].edit}
                                              onClick={() => {
                                                setOrgnizationID(department?.organizationId);
                                                setEditDepartmentId(department?.id);
                                                setEditDepartmentName(department?.name ?? "");
                                                setOpenDepartment(true);
                                              }}
                                            />
                                          )}
                                        </RowActions>
                                      </td>
                                    )}
                                  </tr>
                                  {isDeptExpanded &&
                                    deptProjects.map((project, projIndex) => {
                                      const isEnabled = project?.isDcrcEnabled ?? false;
                                      const isDeleted = project?.isDeleted ?? false;
                                      const isToggling = togglingProjectId === project?.id;
                                      const isTogglingDelete = togglingDeleteProjectId === project?.id;
                                      const toggleBtnClass = (on) =>
                                        `relative inline-flex h-6 w-10 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${theme === "dark" ? "focus:ring-offset-gray-800" : ""} ${on ? (theme === "dark" ? "bg-blue-500" : theme === "high-contrast" ? "bg-blue-700" : "bg-blue-600") : (theme === "dark" ? "bg-gray-600" : theme === "high-contrast" ? "bg-gray-500" : "bg-gray-300")}`;
                                      const thumbClass = (on) =>
                                        `pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${on ? (isRtl ? "-translate-x-4" : "translate-x-4") : "translate-x-0.5"}`;
                                      const labelClass = `text-xs font-semibold whitespace-nowrap min-w-[38px] ${theme === "dark" ? "text-gray-300" : theme === "high-contrast" ? "text-gray-800" : "text-[#4B5563]"}`;
                                      return (
                                        <tr
                                          key={project?.id ?? projIndex}
                                          className="bg-white dark:bg-gray-700/40 text-[#4B5563] text-xs border-b border-b-[#EBECEF] dark:border-gray-600 hover:bg-[#EBF5FF] dark:hover:bg-gray-700/60"
                                        >
                                          <td className="p-2 text-center" />
                                          <td className="p-2 align-middle text-left rtl:text-right">
                                            <NameCell indent={48}   dataTestId={`organization-list-table-td-project-name-${project.organizationId}-${project.departmentId}-${project.id}`}>{project?.name}</NameCell>
                                          </td>
                                          <td className="p-2 align-middle text-center">
                                            <TypeBadge level="project" langMode={langMode} />
                                          </td>
                                          <td className="p-2 align-middle text-gray-400 text-xs text-center">?</td>
                                          {showOrgListActionColumn && (
                                          <td className="p-2 align-middle text-end whitespace-nowrap">
                                            <RowActions>
                                              {canDeleteProject && (
                                                <div className="inline-flex items-center gap-2 flex-shrink-0">
                                                  <span className={labelClass}>DCRC</span>
                                                  <span
                                                    onClick={(e) => handleProjectStatusToggle(project, e)}
                                                    className="inline-flex flex-shrink-0"
                                                    title={isEnabled ? translations[langMode].disable : translations[langMode].Enable}
                                                  >
                                                    <span
                                                      role="button"
                                                      tabIndex={0}
                                                      onKeyDown={(e) => {
                                                        if (e.key === "Enter" || e.key === " ") {
                                                          e.preventDefault();
                                                          handleProjectStatusToggle(project, e);
                                                        }
                                                      }}
                                                      className={`${toggleBtnClass(isEnabled)} ${isToggling ? "opacity-70 pointer-events-none" : ""}`}
                                                      aria-checked={isEnabled}
                                                      aria-label="DCRC"
                                                    >
                                                      <span className={thumbClass(isEnabled)} />
                                                    </span>
                                                  </span>
                                                </div>
                                              )}
                                              {canDeleteProject && (
                                                <span
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setDeleteConfirmProject(project);
                                                  }}
                                                  role="button"
                                                  tabIndex={0}
                                                  onKeyDown={(e) => {
                                                    if (e.key === "Enter" || e.key === " ") {
                                                      e.preventDefault();
                                                      setDeleteConfirmProject(project);
                                                    }
                                                  }}
                                                  className={`inline-flex flex-shrink-0 items-center justify-center p-1.5 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 ${isTogglingDelete ? "opacity-70 pointer-events-none" : ""}`}
                                                  title={isDeleted ? (translations[langMode]?.orgShowProject ?? "Show project") : (translations[langMode]?.orgHideProject ?? "Hide project")}
                                                  aria-label={isDeleted ? (translations[langMode]?.orgShowProject ?? "Show project") : (translations[langMode]?.orgHideProject ?? "Hide project")}
                                                >
                                                  {isDeleted ? (
                                                    <Eye
                                                      className={`flex-shrink-0 ${theme === "dark" ? "text-blue-400" : "text-blue-600"}`}
                                                      size={16}
                                                    />
                                                  ) : (
                                                    <EyeOff
                                                      className={`flex-shrink-0 ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}
                                                      size={16}
                                                    />
                                                  )}
                                                </span>
                                              )}
                                              {showOrgActionColumn && canUpdateProject && (
                                                <EditActionIcon
                                                  isRtl={isRtl}
                                                  title={translations[langMode].edit}
                                                  dataTestId="organization-list-table-td-action-update"
                                                  onClick={() => {
                                                    setEditProject(project);
                                                    setEditProjectDepartmentId(department?.id);
                                                    setOpenEditProject(true);
                                                  }}
                                                />
                                              )}
                                            </RowActions>
                                          </td>
                                          )}
                                        </tr>
                                      );
                                    })}
                                </React.Fragment>
                              );
                            })}
                        </React.Fragment>
                        );
                      })
                    ) : null}

                    {/* Department-only view: flat list of departments */}
                    {viewMode === VIEW_MODE_DEPARTMENT_ONLY && sortedDepartments.map((department, idx) => (
                      <tr
                        key={department?.id ?? idx}
                        className={`${theme === "dark" ? "dark-mode bg-gray-700 border-gray-600" : theme === "high-contrast" ? "high-contrast bg-white" : "bg-white"} border-b`}
                      >
                        <td className="p-2 text-center">{idx + 1}</td>
                        <td className="p-2.5 text-left rtl:text-right text-xs text-[#4B5563]">
                          <NameCell dataTestId={`organization-list-table-td-department-name-${department.organizationId}-${department.id}`}>{department?.name}</NameCell>
                        </td>
                        <td className="p-2 text-center">
                          <TypeBadge level="department" langMode={langMode} />
                        </td>
                        {showDeptListActionColumn && (
                          <td className="p-2 text-end whitespace-nowrap">
                            <RowActions>
                              {canCreateProject && (
                                <AddProjectIcon
                                  isRtl={isRtl}
                                  title={translations[langMode].addnewproject}
                                  onClick={() => {
                                    setOrgnizationID(department?.organizationId);
                                    setDeparmentId(department?.id);
                                    setOpenProject(true);
                                  }}
                                />
                              )}
                              {canUpdateDepartment && (
                                <EditActionIcon
                                  isRtl={isRtl}
                                  title={translations[langMode].edit}
                                  onClick={() => {
                                    setOrgnizationID(department?.organizationId);
                                    setEditDepartmentId(department?.id);
                                    setEditDepartmentName(department?.name ?? "");
                                    setOpenDepartment(true);
                                  }}
                                />
                              )}
                            </RowActions>
                          </td>
                        )}
                      </tr>
                    ))}

                    {/* Project-only view: flat list of projects */}
                    {viewMode === VIEW_MODE_PROJECT_ONLY && sortedProjects.map((project, idx) => {
                      const isEnabled = project?.isDcrcEnabled ?? false;
                      const isDeleted = project?.isDeleted ?? false;
                      const isToggling = togglingProjectId === project?.id;
                      const isTogglingDelete = togglingDeleteProjectId === project?.id;
                      const toggleBtnClass = (on) =>
                        `relative inline-flex h-6 w-10 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${theme === "dark" ? "focus:ring-offset-gray-800" : ""} ${on ? (theme === "dark" ? "bg-blue-500" : theme === "high-contrast" ? "bg-blue-700" : "bg-blue-600") : (theme === "dark" ? "bg-gray-600" : theme === "high-contrast" ? "bg-gray-500" : "bg-gray-300")}`;
                      const thumbClass = (on) =>
                        `pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${on ? (isRtl ? "-translate-x-4" : "translate-x-4") : "translate-x-0.5"}`;
                      const labelClass = `text-xs font-semibold whitespace-nowrap min-w-[38px] ${theme === "dark" ? "text-gray-300" : theme === "high-contrast" ? "text-gray-800" : "text-[#4B5563]"}`;
                      return (
                      <tr
                        key={project?.id ?? idx}
                        className={`${theme === "dark" ? "dark-mode bg-gray-700 border-gray-600" : theme === "high-contrast" ? "high-contrast bg-white" : "bg-white"} border-b`}
                      >
                        <td className="p-2 text-center">{idx + 1}</td>
                        <td className="p-2.5 text-left rtl:text-right text-xs text-[#4B5563]">
                          <NameCell dataTestId={`organization-list-table-td-project-name-${project.organizationId}-${project.departmentId}-${project.id}`}>{project?.name}</NameCell>
                        </td>
                        <td className="p-2 text-center">
                          <TypeBadge level="project" langMode={langMode} />
                        </td>
                        {(canViewDcrc || canUpdateProject) && (
                          <td className="p-2 text-end whitespace-nowrap">
                            <RowActions>
                              {canViewDcrc && (
                                <div className="inline-flex items-center gap-2 flex-shrink-0">
                                  <span className={labelClass}>DCRC</span>
                                  <span
                                    onClick={(e) => handleProjectStatusToggle(project, e)}
                                    className="inline-flex flex-shrink-0"
                                    title={isEnabled ? translations[langMode].disable : translations[langMode].Enable}
                                  >
                                    <span
                                      role="button"
                                      tabIndex={0}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                          e.preventDefault();
                                          handleProjectStatusToggle(project, e);
                                        }
                                      }}
                                      className={`${toggleBtnClass(isEnabled)} ${isToggling ? "opacity-70 pointer-events-none" : ""}`}
                                      aria-checked={isEnabled}
                                      aria-label="DCRC"
                                    >
                                      <span className={thumbClass(isEnabled)} />
                                    </span>
                                  </span>
                                </div>
                              )}
                              {canUpdateDcrc && (
                                <span
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteConfirmProject(project);
                                  }}
                                  role="button"
                                  tabIndex={0}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      setDeleteConfirmProject(project);
                                    }
                                  }}
                                  className={`inline-flex flex-shrink-0 items-center justify-center p-1.5 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 ${isTogglingDelete ? "opacity-70 pointer-events-none" : ""}`}
                                  title={isDeleted ? (translations[langMode]?.orgShowProject ?? "Show project") : (translations[langMode]?.orgHideProject ?? "Hide project")}
                                  aria-label={isDeleted ? (translations[langMode]?.orgShowProject ?? "Show project") : (translations[langMode]?.orgHideProject ?? "Hide project")}
                                >
                                  {isDeleted ? (
                                    <Eye
                                      className={`flex-shrink-0 ${theme === "dark" ? "text-blue-400" : "text-blue-600"}`}
                                      size={16}
                                    />
                                  ) : (
                                    <EyeOff
                                      className={`flex-shrink-0 ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}
                                      size={16}
                                    />
                                  )}
                                </span>
                              )}
                              {(canViewDcrc || canUpdateProject) && (
                                <EditActionIcon
                                  isRtl={isRtl}
                                  title={translations[langMode].edit}
                                  dataTestId="organization-list-table-td-action-update"
                                onClick={() => {
                                    setEditProject(project);
                                    setEditProjectDepartmentId(project?.departmentId);
                                    setOpenEditProject(true);
                                  }}
                                />
                              )}
                            </RowActions>
                          </td>
                        )}
                      </tr>
                      );
                    })}

                    {/* Department + Project view: departments with expandable projects */}
                    {viewMode === VIEW_MODE_DEPARTMENT_PROJECT && sortedDepartments.map((department, depIndex) => {
                      const deptProjects = getProjectsForDepartment(department?.id);
                      const isDeptExpanded = openDepartmentId === department?.id;
                      return (
                      <React.Fragment key={department?.id ?? depIndex}>
                        <tr
                          className={`cursor-pointer ${theme === "dark" ? "dark-mode bg-gray-700 border-gray-600" : "bg-white"} border-b hover:bg-blue-50/50 dark:hover:bg-gray-600/50`}
                          onClick={() => toggleDepartment(department?.id)}
                        >
                          <td className="p-2 text-center">
                            <div className="flex items-center gap-2">
                              <ExpandChevron expanded={isDeptExpanded} isRtl={isRtl} />
                              <span>{depIndex + 1}</span>
                            </div>
                          </td>
                          <td className="p-2.5 text-left rtl:text-right text-xs text-[#4B5563]">
                            <div className="flex items-center gap-2">
                              <NameCell dataTestId={`organization-list-table-td-department-name-${department.organizationId}-${department.id}`}>{department?.name}</NameCell>
                              {deptProjects.length > 0 && (
                                <span className="text-xs text-gray-400">({deptProjects.length})</span>
                              )}
                            </div>
                          </td>
                          <td className="p-2 text-center">
                            <TypeBadge level="department" langMode={langMode} />
                          </td>
                          {showDeptProjectListActionColumn && (
                            <td className="p-2 text-end whitespace-nowrap">
                              <RowActions>
                                {canCreateProject && (
                                  <AddProjectIcon
                                    isRtl={isRtl}
                                    title={translations[langMode].addnewproject}
                                    onClick={() => {
                                      setOrgnizationID(department?.organizationId);
                                      setDeparmentId(department?.id);
                                      setOpenProject(true);
                                    }}
                                  />
                                )}
                                {canUpdateDepartment && (
                                  <EditActionIcon
                                    title={translations[langMode].edit}
                                    onClick={() => {
                                      setOrgnizationID(department?.organizationId);
                                      setEditDepartmentId(department?.id);
                                      setEditDepartmentName(department?.name ?? "");
                                      setOpenDepartment(true);
                                    }}
                                  />
                                )}
                              </RowActions>
                            </td>
                          )}
                        </tr>
                        {isDeptExpanded && deptProjects.map((project, projIndex) => {
                            const isEnabled = project?.isDcrcEnabled ?? false;
                            const isDeleted = project?.isDeleted ?? false;
                            const isToggling = togglingProjectId === project?.id;
                            const isTogglingDelete = togglingDeleteProjectId === project?.id;
                            const toggleBtnClass = (on) =>
                              `relative inline-flex h-6 w-10 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${theme === "dark" ? "focus:ring-offset-gray-800" : ""} ${on ? (theme === "dark" ? "bg-blue-500" : theme === "high-contrast" ? "bg-blue-700" : "bg-blue-600") : (theme === "dark" ? "bg-gray-600" : theme === "high-contrast" ? "bg-gray-500" : "bg-gray-300")}`;
                            const thumbClass = (on) =>
                              `pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${on ? (isRtl ? "-translate-x-4" : "translate-x-4") : "translate-x-0.5"}`;
                            const labelClass = `text-xs font-semibold whitespace-nowrap min-w-[38px] ${theme === "dark" ? "text-gray-300" : theme === "high-contrast" ? "text-gray-800" : "text-[#4B5563]"}`;
                            return (
                            <tr key={project?.id ?? projIndex} className="bg-slate-50/80 dark:bg-gray-800/60 text-[#4B5563] text-xs border-b border-b-[#EBECEF] dark:border-gray-600 hover:bg-[#EBF5FF]">
                              <td className="p-2 text-center" />
                              <td className="p-2 text-left rtl:text-right">
                                <NameCell indent={32} dataTestId={`organization-list-table-td-project-name-${project.organizationId}-${project.departmentId}-${project.id}`}>{project?.name}</NameCell>
                              </td>
                              <td className="p-2 text-center">
                                <TypeBadge level="project" langMode={langMode} />
                              </td>
                              {showDeptProjectListActionColumn && (
                                <td className="p-2 text-end whitespace-nowrap">
                                  <RowActions>
                                    {canViewDcrc && (
                                      <div className="inline-flex items-center gap-2 flex-shrink-0">
                                        <span className={labelClass}>DCRC</span>
                                        <span
                                          onClick={(e) => handleProjectStatusToggle(project, e)}
                                          className="inline-flex flex-shrink-0"
                                          title={isEnabled ? translations[langMode].disable : translations[langMode].Enable}
                                        >
                                          <span
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => {
                                              if (e.key === "Enter" || e.key === " ") {
                                                e.preventDefault();
                                                handleProjectStatusToggle(project, e);
                                              }
                                            }}
                                            className={`${toggleBtnClass(isEnabled)} ${isToggling ? "opacity-70 pointer-events-none" : ""}`}
                                            aria-checked={isEnabled}
                                            aria-label="DCRC"
                                          >
                                            <span className={thumbClass(isEnabled)} />
                                          </span>
                                        </span>
                                      </div>
                                    )}
                                    {canUpdateDcrc && (
                                      <span
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setDeleteConfirmProject(project);
                                        }}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                            setDeleteConfirmProject(project);
                                          }
                                        }}
                                        className={`inline-flex flex-shrink-0 items-center justify-center p-1.5 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 ${isTogglingDelete ? "opacity-70 pointer-events-none" : ""}`}
                                        title={isDeleted ? (translations[langMode]?.orgShowProject ?? "Show project") : (translations[langMode]?.orgHideProject ?? "Hide project")}
                                        aria-label={isDeleted ? (translations[langMode]?.orgShowProject ?? "Show project") : (translations[langMode]?.orgHideProject ?? "Hide project")}
                                      >
                                        {isDeleted ? (
                                          <Eye
                                            className={`flex-shrink-0 ${theme === "dark" ? "text-blue-400" : "text-blue-600"}`}
                                            size={16}
                                          />
                                        ) : (
                                          <EyeOff
                                            className={`flex-shrink-0 ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}
                                            size={16}
                                          />
                                        )}
                                      </span>
                                    )}
                                    {(canViewDcrc || canUpdateProject) && (
                                      <EditActionIcon
                                        isRtl={isRtl}
                                        title={translations[langMode].edit}
                                        dataTestId="organization-list-table-td-action-update"
                                        onClick={() => {
                                          setEditProject(project);
                                          setEditProjectDepartmentId(department?.id);
                                          setOpenEditProject(true);
                                        }}
                                      />
                                    )}
                                  </RowActions>
                                </td>
                              )}
                            </tr>
                            );
                          })}
                      </React.Fragment>
                      );
                    })}

                    {/* No data row */}
                    {viewMode === VIEW_MODE_ORGANIZATION && filteredSortedOrganizations.length === 0 && (
                      <tr>
                        <td colSpan={4 + (showOrgListActionColumn ? 1 : 0)} className="text-center py-8">{translations[langMode].noOrg}</td>
                      </tr>
                    )}
                    {viewMode === VIEW_MODE_DEPARTMENT_ONLY && sortedDepartments.length === 0 && (
                      <tr>
                        <td colSpan={3 + (showDeptListActionColumn ? 1 : 0)} className="text-center py-8">{translations[langMode].noOrg}</td>
                      </tr>
                    )}
                    {viewMode === VIEW_MODE_PROJECT_ONLY && sortedProjects.length === 0 && (
                      <tr>
                        <td colSpan={3 + (canUpdateProject ? 1 : 0)} className="text-center py-8">{translations[langMode].noOrg}</td>
                      </tr>
                    )}
                    {viewMode === VIEW_MODE_DEPARTMENT_PROJECT && sortedDepartments.length === 0 && (
                      <tr>
                        <td colSpan={3 + (showDeptProjectListActionColumn ? 1 : 0)} className="text-center py-8">{translations[langMode].noOrg}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              {loading && <Spinner />}
        </div>
      </div>
      <CreateOrganization
        open={open}
        setOpen={setOpen}
        fetchorgnizationlist={fetchorgnizationlist}
      />
      <CreateDepartment
        open={openDepartment}
        organizationID={orgnizationId}
        setgetDeparment={setgeDeparment}
        onDepartmentSaved={(orgId, result) => {
          setgeDeparment((prev) => (prev || []).filter((d) => String(d?.organizationId) !== String(orgId)).concat(Array.isArray(result) ? result : []));
        }}
        setOpen={(v) => {
          if (!v) {
            setEditDepartmentId(null);
            setEditDepartmentName("");
          }
          setOpenDepartment(v);
        }}
        editDepartmentId={editDepartmentId}
        editDepartmentName={editDepartmentName}
        setEditDepartmentId={setEditDepartmentId}
        setEditDepartmentName={setEditDepartmentName}
      />
      <CreateProject
        open={openProject}
        setProjectData={setProjectData}
        deparmentId={deparmentId}
        setOpen={setOpenProject}
      />
      <EditProject
        open={openEditProject}
        setOpen={setOpenEditProject}
        project={editProject}
        departmentId={editProjectDepartmentId}
        setProjectData={setProjectData}
      />
      <Updateorgnization
        setUpdateOpen={setUpdateOpen}
        fetchorgnizationlist={fetchorgnizationlist}
        updateopen={updateopen}
        setEditOrganizationName={setEditOrganizationName}
        editOrganizationname={editOrganizationname}
        setEditOrganizationImg={setEditOrganizationImg}
        editOrganizationimg={editOrganizationimg}
        editReportEmails={editReportEmails}
        setEditReportEmails={setEditReportEmails}
        orgnizationId={orgnizationId}
        isIms={isIms}
        setIsIMS={setIsIMS}
      />
      <Transition.Root show={!!deleteConfirmProject} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setDeleteConfirmProject(null)}
        >
          <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
          </Transition.Child>
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className={`w-full max-w-md rounded-2xl p-6 shadow-xl ${theme === "dark" ? "bg-[#1E1E1E] text-white" : theme === "high-contrast" ? "bg-white text-black border border-black" : "bg-white text-gray-900"}`}>
                <div className="flex items-start gap-4">
                  <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${deleteConfirmProject?.isDeleted ? "bg-blue-100 dark:bg-blue-900/30" : "bg-amber-100 dark:bg-amber-900/30"}`}>
                    {deleteConfirmProject?.isDeleted ? (
                      <Eye className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    ) : (
                      <EyeOff className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <Dialog.Title className="text-lg font-semibold">
                      {deleteConfirmProject?.isDeleted
                        ? (translations[langMode]?.orgShowProjectTitle ?? "Show project?")
                        : (translations[langMode]?.orgHideProjectTitle ?? "Hide project?")}
                    </Dialog.Title>
                    <p className={`mt-2 text-sm ${theme === "dark" ? "text-gray-300" : "text-gray-600"}`}>
                      {deleteConfirmProject?.isDeleted
                        ? (translations[langMode]?.orgShowProjectDesc ?? "Show \"{name}\" again? It will be visible in the list.").replace("{name}", deleteConfirmProject?.name ?? "")
                        : (translations[langMode]?.orgHideProjectDesc ?? "Hide \"{name}\" from the list? You can show it again later from the same place.").replace("{name}", deleteConfirmProject?.name ?? "")}
                    </p>
                  </div>
                </div>
                <div className="mt-6 flex justify-end gap-3">
                  <CommonButton
                    onClick={() => setDeleteConfirmProject(null)}
                    variant="outline"
                    tooltipPosition="top"
                  >
                    {translations[langMode].cancel}
                  </CommonButton>
                  <CommonButton colorIntent={deleteConfirmProject?.isDeleted ? "success" : "warning"}
                    onClick={(e) => deleteConfirmProject && handleProjectDeleteToggle(deleteConfirmProject, e)}
                    variant="primary"
                    tooltipPosition="top"
                  >
                    {deleteConfirmProject?.isDeleted
                      ? (translations[langMode]?.orgShowProject ?? "Show project")
                      : (translations[langMode]?.orgHideProject ?? "Hide project")}
                  </CommonButton>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
      <Transition.Root show={!!deleteConfirmOrganization} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          initialFocus={deleteOrgModalInitialFocusRef}
          onClose={() => setDeleteConfirmOrganization(null)}
        >
          <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
          </Transition.Child>
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel
                dir={isRtl ? "rtl" : "ltr"}
                className={`w-full max-w-md rounded-2xl p-6 shadow-xl ${theme === "dark" ? "bg-[#1E1E1E] text-white" : theme === "high-contrast" ? "bg-white text-black border border-black" : "bg-white text-gray-900"}`}
              >
                <button
                  ref={deleteOrgModalInitialFocusRef}
                  type="button"
                  tabIndex={-1}
                  aria-hidden="true"
                  className="sr-only"
                />
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                    <Trash2 className="h-5 w-5 text-red-600 dark:text-red-400" />
                  </div>
                  <div className={`flex-1 ${isRtl ? "text-right" : "text-left"}`}>
                    <Dialog.Title className="text-lg font-semibold">
                      {deleteOrganizationTitle}
                    </Dialog.Title>
                    <p className={`mt-2 text-sm ${theme === "dark" ? "text-gray-300" : "text-gray-600"}`}>
                      {deleteOrganizationMessage}
                    </p>
                  </div>
                </div>
                <div className="mt-6 flex justify-end gap-3">
                  <CommonButton
                    onClick={() => setDeleteConfirmOrganization(null)}
                    disabled={isDeletingOrganization}
                    variant="outline"
                    className="rounded-lg"
                  >
                    {translations[langMode].cancle || translations[langMode].cancel || "Cancel"}
                  </CommonButton>
                  <CommonButton colorIntent="danger"
                    onClick={handleOrganizationDeleteConfirm}
                    disabled={isDeletingOrganization}
                    variant="primary"
                    className={`rounded-lg ${isDeletingOrganization ? "bg-opacity-60 cursor-not-allowed" : "bg-opacity-100"}`}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      <Trash2 size={16} className="shrink-0" />
                      <span>
                        {isDeletingOrganization
                          ? translations[langMode].Processing
                          : (translations[langMode].delete || "Delete")}
                      </span>
                    </span>
                  </CommonButton>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};
export default OrgnizationList;
